import React, { useState, useEffect, useCallback } from "react";
import { IonPage, IonContent, IonHeader, useIonViewWillEnter, IonToolbar, IonButtons, IonButton, IonIcon, IonAlert, IonGrid, IonRow, IonCol } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { Haptics } from '@capacitor/haptics';
import { useHistory } from "react-router-dom";
import './PinVerifyPage.css';
import KeyPad from "../../../components/KeyPad/KeyPad";
import { useAppDispatch, useAppSelector } from "../../../services/redux/hook";
import { setPIN } from "../../../services/redux/slices/pin/pin";
import AppHeader from "../../../components/AppHeader/AppHeader";

const PinVerifyPage: React.FC<any> = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useAppDispatch();
    
    // Get flow context to determine where to redirect after PIN verification
    const flowContext = useAppSelector((state) => state.flowContext);
    const [verifyPin, setVerifyPin] = useState(['', '', '', '', '', '']);
    const [isIncorrect, setIsIncorrect] = useState(false);
    const [pin, setPin] = useState('');
   
    // Reset the PIN input fields when entering the view
    useIonViewWillEnter(() => {
        setVerifyPin(['', '', '', '', '', '']);
    });

    // Helper to reset PIN and set error
    const resetPinInput = useCallback(() => {
        setVerifyPin(['', '', '', '', '', '']);
        setIsIncorrect(false);
    }, []);

    // Verifies the pin after user inputs all digits
    const verify = useCallback(
        async (inputPin: string) => {
            if (inputPin === pin) {
                // PIN is correct
                dispatch(setPIN(pin));
                
                // Use flow context to determine where to redirect
                const redirectPath = flowContext.redirectAfterPin;

                if (redirectPath) {
                    // Navigate to the next page - don't clear flow context here
                    // It will be cleared when the entire flow (registration/login/migration) completes
                    history.push(redirectPath);
                }
                
            } else {
                // PIN is incorrect
                setIsIncorrect(true);
                await Haptics.vibrate({ duration: 800 });
                setTimeout(resetPinInput, 800);
            }
        },
        [pin, dispatch, history, resetPinInput, flowContext]
    );


    // Handles logic when the pin input is complete
    const modelChange = useCallback(() => {
        const inputCombination = verifyPin.join("");
        verify(inputCombination);
    }, [verifyPin, verify]);

    // Handle the keypad click
    const handleClick = useCallback(
        (value: string) => {
            const updatedPin = [...verifyPin];
            const emptyIndex = updatedPin.indexOf('');
            if (emptyIndex !== -1) {
                updatedPin[emptyIndex] = value;
                setVerifyPin(updatedPin);
            }
        },
        [verifyPin]
    );

    // Handle removing a digit from the PIN
    const handleRemove = useCallback(() => {
        const updatedPin = [...verifyPin];
        const firstEmpty = updatedPin.indexOf('');
        const indexToClear = firstEmpty === -1 ? updatedPin.length - 1 : firstEmpty - 1;
        if (indexToClear >= 0) {
            updatedPin[indexToClear] = '';
            setVerifyPin(updatedPin);
        }
    }, [verifyPin]);



    // Whenever the PIN changes, check if it’s complete
    useEffect(() => {
        if (verifyPin.every((digit) => digit !== '')) {
            modelChange();
        }
    }, [verifyPin, modelChange]);

    // Set initial PIN from the route state
    useEffect(() => {
        setVerifyPin(['', '', '', '', '', '']);
        const location: any = history.location;
        const initialPin = location?.state?.detail?.pin || '';
        setPin(initialPin);
    }, [history]);


    return (
        <IonPage>

            <IonHeader className="ion-no-border">
                <AppHeader
                    title={t('pin.verify-pin.HEADER') as string}
                    goBack={true}
                />
            </IonHeader>

            <IonContent scrollY={false}>

                <div className="ion-padding">
                    <p className="ion-text-center">{t('pin.verify-pin.DIGIT-LABELTEXT-CONT')}</p>
                </div>

                <IonGrid className="pinlock-PIN">
                    <IonRow>
                        {verifyPin.map((value, index) => (
                            <IonCol size="2" key={index} className="ion-text-center">
                                <div className={isIncorrect ? "animated-shake" : ""}>
                                    <div className={`${value !== '' ? 'pinlock-otp-input__active' : 'pinlock-otp-input'}`} />
                                </div>
                            </IonCol>
                        ))}
                    </IonRow>
                </IonGrid>

                <IonGrid className="pinlock-pinKeyPad">
                    <KeyPad handleClick={handleClick} handleRemove={handleRemove} compact />
                </IonGrid>
      
            </IonContent>

        </IonPage>
    );
};

export default PinVerifyPage;


